import { ApolloClient } from '@apollo/client/core';
import { MapFilterValuesPipe } from 'shared/resolvers/MapFilterValues.pipe';
import { AttributesClassResolver } from 'shared/resolvers/AttributesClassResolver';
import { LocalesEnum } from 'shared/enums/LocalesEnum';
import { RegionsEnum } from 'shared/enums/RegionsEnum';
import { getDefaultLocale } from 'shared/mappers/getDefaultLocale';
import { stringToEnum } from 'src/utils/stringToEnum';
import { createResolver } from 'src/database/createResolver.ts';
import { createMapToCombo, createMapToProduct, createMapToRankedProduct } from 'src/database/createMapToProduct.ts';
import { ModuleEnum } from 'shared/enums/ModuleEnum.ts';
import { AttributesFilterInputInterface } from 'shared/interfaces/AttributesFilterInputInterface.ts';
import { VssResolver } from 'shared/resolvers/VssResolver.ts';
import { mapToCount } from 'shared/mappers/mapToCount.ts';
import { mapToValues } from 'shared/mappers/mapToValues.ts';
import { SensorsResolver } from 'shared/resolvers/SensorsResolver.ts';
import { OrderByInterface } from 'shared/interfaces/OrderByInterface.ts';
import { FilterValuesResolver } from 'shared/resolvers/FilterValuesResolver.ts';
import { createMapFBD } from 'shared/mappers/createMapFBD.ts';
import { DatabaseClient } from './databaseClient';

const params = {
    [RegionsEnum.eu]: {
        preferredAttr: '2797',
        boostAttr: '8342',
        torqueAttr: '260',
        discontinued: '474',
    },
    [RegionsEnum.ap]: {
        preferredAttr: '2796',
        boostAttr: '8385',
        torqueAttr: '260',
        discontinued: '472',
    },
    [RegionsEnum.am]: {
        preferredAttr: '2791',
        boostAttr: '7015',
        torqueAttr: '259',
        discontinued: '470',
    },
};

export function setResolvers(
    apollo: ApolloClient<unknown>,
    database: DatabaseClient,
    regionStr: string,
    currentLocaleStr: string,
) {
    const region = stringToEnum<RegionsEnum>(RegionsEnum, regionStr);
    const currentLocale = stringToEnum<LocalesEnum>(LocalesEnum, currentLocaleStr);
    const rawName = getDefaultLocale(region);
    const mapFilterValuesPipe = new MapFilterValuesPipe();
    const attributesClassResolver = new AttributesClassResolver();
    const mapToProduct = createMapToProduct(rawName);
    const mapToRankedProduct = createMapToRankedProduct(region);
    const mapToCombo = createMapToCombo(region);
    const mapToFBD = createMapFBD(rawName);
    const filterValuesResolver = new FilterValuesResolver(
        database,
        mapToValues,
        mapToFBD,
    );
    const vssResolver = new VssResolver(
        database,
        mapToRankedProduct,
        mapToCount,
        mapToValues,
        mapToProduct,
        mapToCombo,
        params[region].preferredAttr,
        params[region].boostAttr,
        params[region].torqueAttr,
        params[region].discontinued,
        region,
    );
    const sensorsResolver = new SensorsResolver(
        database,
        mapToValues,
        mapToProduct,
        params[region].discontinued,
    );

    apollo.setResolvers({
        Query: {
            values: createResolver(async (root, args: {
                id: string;
                module: ModuleEnum;
                filter: AttributesFilterInputInterface;
            }) => {
                if (args) {
                    const { id, module, filter } = args;
                    return filterValuesResolver.values(
                        id,
                        module,
                        mapFilterValuesPipe.transform(filter),
                        rawName,
                        currentLocale,
                    );
                }
                return root.values ?? null;
            }),
            sensorsValues: createResolver((root, args: {
                id: string;
                filter: AttributesFilterInputInterface;
                skipExclude: boolean;
            }) => {
                if (args) {
                    return sensorsResolver.sensorsValues(
                        args.id,
                        mapFilterValuesPipe.transform(args.filter),
                        rawName,
                        currentLocale,
                        args.skipExclude,
                    );
                }
                return root.sensorsValues ?? null;
            }),
            sensorAccessories: createResolver((root, args: {
                sensorLabel: string;
                filter: AttributesFilterInputInterface;
            }) => {
                if (args) {
                    return sensorsResolver.sensorAccessories(
                        rawName,
                        args.sensorLabel,
                        mapFilterValuesPipe.transform(args.filter),
                    );
                }
                return root.sensorAccessories ?? null;
            }),
            devicesList: createResolver((root, args: {
                module: ModuleEnum;
                offset: number;
                limit: number;
                filter: AttributesFilterInputInterface;
                orderByString: OrderByInterface[];
                orderByNumber: OrderByInterface[];
            }) => {
                if (args) {
                    return vssResolver.list(
                        args.module,
                        args.offset,
                        args.limit,
                        mapFilterValuesPipe.transform(args.filter),
                        rawName,
                        args.orderByNumber,
                        args.orderByString,
                    );
                }
                return root.devicesList ?? null;
            }),
            actuatorsList: createResolver((root, args: {
                module: ModuleEnum;
                offset: number;
                limit: number;
                filter: AttributesFilterInputInterface;
                orderByString: OrderByInterface[];
                orderByNumber: OrderByInterface[];
            }) => {
                if (args) {
                    return vssResolver.actuatorsList(
                        args.module,
                        args.offset,
                        args.limit,
                        mapFilterValuesPipe.transform(args.filter),
                        rawName,
                        args.orderByNumber,
                        args.orderByString,
                    );
                }
                return root.actuatorsList ?? null;
            }),
            vssActuators: createResolver((root, args: {
                label: string;
                offset: number;
                limit: number;
                filter: AttributesFilterInputInterface;
                orderByString: OrderByInterface[];
                orderByNumber: OrderByInterface[];
            }) => {
                if (args) {
                    return vssResolver.vssActuators(
                        args.offset,
                        args.limit,
                        args.label,
                        mapFilterValuesPipe.transform(args.filter),
                        rawName,
                        args.orderByNumber,
                        args.orderByString,
                    );
                }
                return root.vssActuators ?? null;
            }),
            vssList: createResolver((root, args: {
                volumetricFlow: string;
                volumetricFlow2: string;
                kvs: string;
                kvs2: string;
                minDN: string;
                offset: number;
                limit: number;
                filter: AttributesFilterInputInterface;
                orderByString: OrderByInterface[];
                orderByNumber: OrderByInterface[];
            }) => {
                if (args) {
                    return vssResolver.vssList(
                        args.offset,
                        args.limit,
                        mapFilterValuesPipe.transform(args.filter),
                        rawName,
                        args.volumetricFlow,
                        args.volumetricFlow2,
                        args.kvs,
                        args.kvs2,
                        args.minDN,
                        args.orderByNumber,
                        args.orderByString,
                    );
                }
                return root.vssList ?? null;
            }),
            vssCount: createResolver((root, args: {
                volumetricFlow: string;
                volumetricFlow2: string;
                kvs: string;
                kvs2: string;
                minDN: string;
                filter: AttributesFilterInputInterface;
            }) => {
                if (args) {
                    return vssResolver.vssCount(
                        args.volumetricFlow,
                        mapFilterValuesPipe.transform(args.filter),
                        rawName,
                        args.volumetricFlow2,
                        args.kvs,
                        args.kvs2,
                        args.minDN,
                    );
                }
                return root.vssCount ?? null;
            }),
            vssValues: createResolver((root, args: {
                volumetricFlow: string;
                volumetricFlow2: string;
                kvs: string;
                kvs2: string;
                minDN: string;
                id: string;
                filter: AttributesFilterInputInterface;
                labelId: string;
            }) => {
                if (args) {
                    return vssResolver.vssValues(
                        args.id,
                        args.volumetricFlow,
                        mapFilterValuesPipe.transform(args.filter),
                        rawName,
                        currentLocale,
                        args.volumetricFlow2,
                        args.kvs,
                        args.kvs2,
                        args.minDN,
                        args.labelId,
                    );
                }
                return root.vssValues ?? null;
            }),
            UserInputKind: createResolver((root, args: {
                filter: AttributesFilterInputInterface;
            }) => {
                if (args) {
                    return vssResolver.userInputKind(
                        mapFilterValuesPipe.transform(args.filter),
                        rawName,
                    );
                }
                return root.UserInputKind ?? null;
            }),
            vssAccessories: createResolver((root, args: {
                valveLabel: string;
                filter: AttributesFilterInputInterface;
                actuatorLabel?: string;
            }) => {
                if (args) {
                    return vssResolver.vssAccessories(
                        rawName,
                        mapFilterValuesPipe.transform(args.filter),
                        args.valveLabel,
                        args.actuatorLabel,
                    );
                }
                return root.vssAccessories ?? null;
            }),
            vssActuatorsValues: createResolver((root, args: {
                id: string;
                label: string;
                filter: AttributesFilterInputInterface;
            }) => {
                if (args) {
                    return vssResolver.vssActuatorsValues(
                        args.id,
                        args.label,
                        mapFilterValuesPipe.transform(args.filter),
                        rawName,
                        currentLocale,
                    );
                }
                return root.vssActuatorsValues ?? null;
            }),
            vssCombo: createResolver((root, args: {
                valveLabel: string;
                actuatorLabel: string;
            }) => {
                if (args) {
                    return vssResolver.vssCombo(
                        rawName,
                        args.valveLabel,
                        args.actuatorLabel,
                    );
                }
                return root.vssCombo ?? null;
            }),
            localized: createResolver((root) => {
                if (!root) {
                    return null;
                }
                const localized = root[currentLocale];
                return !!localized && localized.length > 0 ? localized : (root[rawName] || []);
            }),
            raw: createResolver((root) => {
                if (!root) {
                    return null;
                }
                return root[rawName] || [];
            }),
            _7683: createResolver(async (root) => attributesClassResolver.failSafeFunction(root, rawName)),
            _8160: createResolver(async (root) => attributesClassResolver.failSafeFunctionSolution(root, rawName)),
            _7684: createResolver(async (root) => attributesClassResolver.integratedSwitch(root, rawName)),
            _112: createResolver(async (root) => attributesClassResolver.integratedSwitchSolution(root, rawName)),
            calcMinReqDpv: createResolver(async (root, args: { volumetricFlow: string }) => (region === RegionsEnum.am
                ? null
                : attributesClassResolver.calcMinReqDpvFunction(root, rawName, args.volumetricFlow))),
            calcEffectivePV: createResolver(async (
                root,
                args: { volumetricFlow: string; kvs: string },
            ) => (region === RegionsEnum.am
                ? null
                : attributesClassResolver.calcEffectivePVFunction(root, rawName, args.volumetricFlow, args.kvs))),
            calcEffectiveDpv: createResolver(async (
                root,
                args: { volumetricFlow: string },
            ) => (region === RegionsEnum.am
                ? attributesClassResolver.calcEffectiveDpvFunction_AM(root, rawName, args.volumetricFlow)
                : attributesClassResolver.calcEffectiveDpvFunction(root, rawName, args.volumetricFlow))),
            getClipPosition: createResolver(async (root, args: { volumetricFlow: string }) => (region === RegionsEnum.am
                ? attributesClassResolver.getClipPosition_AM(root, rawName, args.volumetricFlow)
                : attributesClassResolver.getClipPosition_EU(root, rawName, args.volumetricFlow))),
            calcFlowVelocity: createResolver(async (
                root,
                args: { volumetricFlow: string },
            ) => (region === RegionsEnum.am
                ? attributesClassResolver.calcFlowVelocityFunction_AM(root, rawName, args.volumetricFlow)
                : attributesClassResolver.calcFlowVelocityFunction(root, rawName, args.volumetricFlow))),
            getClipPositionPI: createResolver(async (
                root,
                args: { volumetricFlow: string },
            ) => (region === RegionsEnum.am
                ? attributesClassResolver.getClipPositionPIQCV_AM(root, rawName, args.volumetricFlow)
                : attributesClassResolver.getClipPositionPIQCV_EU(root, rawName, args.volumetricFlow))),
        },
    });
}
